import { MoveHorizontal } from "lucide-react";
import React from "react";
import { Marquee } from "@/components/ui/marquee";

// Komponent podglądu
export const MarqueePreview = () => (
  <div className="w-full overflow-hidden py-4">
    <Marquee pauseOnHover className="py-2">
      {Array.from({ length: 8 }, (_, i) => (
        <div
          className="flex justify-center items-center mx-4 w-[150px] h-[80px] 
                   border border-border rounded-xl bg-muted/50"
          key={i}
        >
          Item {i + 1}
        </div>
      ))}
    </Marquee>
  </div>
);

const component = {
  id: "marquee",
  title: "Marquee",
  component: Marquee,
  description: "A component that creates a smooth scrolling marquee effect.",
  preview: MarqueePreview,
  icon: <MoveHorizontal className="h-4 w-4 mr-2" />,
  code: `import { Marquee } from "@/components/ui/marquee";

export const MarqueeExample = () => {
  return (
    <Marquee pauseOnHover className="py-4">
      {Array.from({ length: 8 }, (_, i) => (
        <div
          className="flex justify-center items-center mx-4 w-[150px] h-[80px] 
                   border border-border rounded-xl bg-muted/50"
          key={i}
        >
          Item {i + 1}
        </div>
      ))}
    </Marquee>
  );
};`,
  installation: "This component is already included in the project.",
  detailedInstallation: `
### Installation Steps

1. This component is part of the core UI library in the project.
2. If you're using this in a new project, copy the following files:
   - \`src/components/ui/marquee.jsx\`
   - Add the required CSS animations to your CSS file

### CSS Animations Required

\`\`\`css
@keyframes marquee {
  from { transform: translateX(0); }
  to { transform: translateX(calc(-100% - var(--gap))); }
}

@keyframes marquee-vertical {
  from { transform: translateY(0); }
  to { transform: translateY(calc(-100% - var(--gap))); }
}

.animate-marquee {
  animation: marquee var(--duration) linear infinite;
}

.animate-marquee-vertical {
  animation: marquee-vertical var(--duration) linear infinite;
}
\`\`\`

### Dependencies

\`\`\`json
{
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}
\`\`\`
`,
  sourceCode: `import { cn } from "@/lib/utils";

export function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  children,
  vertical = false,
  repeat = 4,
  ...props
}) {
  return (
    <div
      {...props}
      className={cn(
        "group flex overflow-hidden p-2 [--duration:40s] [--gap:1rem] [gap:var(--gap)]",
        {
          "flex-row": !vertical,
          "flex-col": vertical,
        },
        className
      )}
    >
      {Array(repeat)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn("flex shrink-0 justify-around [gap:var(--gap)]", {
              "animate-marquee flex-row": !vertical,
              "animate-marquee-vertical flex-col": vertical,
              "group-hover:[animation-play-state:paused]": pauseOnHover,
              "[animation-direction:reverse]": reverse,
            })}
          >
            {children}
          </div>
        ))}
    </div>
  );
}`,
  props: [
    {
      name: "className",
      type: "string",
      default: "''",
      description: "Additional CSS classes to apply to the component.",
    },
    {
      name: "reverse",
      type: "boolean",
      default: "false",
      description:
        "Whether the marquee should scroll in the reverse direction.",
    },
    {
      name: "pauseOnHover",
      type: "boolean",
      default: "false",
      description: "Whether the marquee should pause scrolling when hovered.",
    },
    {
      name: "vertical",
      type: "boolean",
      default: "false",
      description:
        "Whether the marquee should scroll vertically instead of horizontally.",
    },
    {
      name: "repeat",
      type: "number",
      default: "4",
      description:
        "Number of times to repeat the children to ensure continuous scrolling.",
    },
    {
      name: "children",
      type: "ReactNode",
      default: "",
      description: "Content to be displayed in the marquee.",
    },
  ],
  examples: [
    {
      title: "Basic horizontal marquee",
      code: `<Marquee>
  <div className="mx-4">Item 1</div>
  <div className="mx-4">Item 2</div>
  <div className="mx-4">Item 3</div>
  <div className="mx-4">Item 4</div>
</Marquee>`,
    },
    {
      title: "Vertical marquee",
      code: `<Marquee vertical className="h-[300px]">
  <div className="my-4">Item 1</div>
  <div className="my-4">Item 2</div>
  <div className="my-4">Item 3</div>
  <div className="my-4">Item 4</div>
</Marquee>`,
    },
    {
      title: "Marquee with pause on hover",
      code: `<Marquee pauseOnHover>
  <div className="mx-4">Hover to pause</div>
  <div className="mx-4">This will stop</div>
  <div className="mx-4">When you hover</div>
  <div className="mx-4">Over the marquee</div>
</Marquee>`,
    },
    {
      title: "Reverse direction marquee",
      code: `<Marquee reverse>
  <div className="mx-4">Going the other way</div>
  <div className="mx-4">Right to left</div>
  <div className="mx-4">Instead of left to right</div>
</Marquee>`,
    },
  ],
};

export default component;
